import { RawDraftContentState } from 'draft-js';

import { ICosts } from 'components/Pages/Admin/Itinerary/Costs/types';
import { EDetailsMode } from 'components/Pages/Admin/Itinerary/Details/types';
import { IAdditionalInfoProps, IBlocksProps, IGuestsProps } from 'components/shared/ArrayFields/types';
import { TBlock } from 'detailsv2/__blocks';
import {
  ITravelBrief,
  ITravelBriefDay,
  TDate,
} from 'detailsv2/LandingV2/Page/RightSide/blocks/Overview/TravelBriefTable/types';
import { EPageRole, EPageType } from 'detailsv2/LandingV2/Page/types';
import { ILandingConfig, ILandingSettings } from 'detailsv2/LandingV2/types';
import { EDateFormat, EOrganizedBy, TTripEvent } from 'detailsv2/structure';
import { IBrandSettings, ISubscriptions } from 'utils/types';

import { SCHEDULE_ITEM_TYPES } from './constants';

export interface IRange {
  end: string | null;
  start: string | null;
}

export interface IPage {
  blocks: IBlocksProps[] | TBlock[];
  customStyles: string;
  datesRange: IRange;
  daysRange: IRange;
  empty?: boolean;
  // TODO check this key for usage and remove from backend response
  forItinerary?: boolean;
  // TODO can be omitted from be side
  handle?: string;
  headline: string;
  // whether page is hidden on client facing itinerary
  hidden: boolean;
  id: number;
  location?: any;
  // TODO can be omitted from be side
  originalId: number | null;
  primaryImageCoords: string[];
  primaryImageId: number | null;
  primaryImageManual: boolean;
  primaryImageUrl: string | null;
  primaryImageVersion400: string | null;
  primaryImageVersion800: string | null;
  primaryImageVersion1200: string | null;
  primaryImageVersion1600: string | null;
  primaryImageVersion2400: string | null;
  primaryImageVersionEmail: string | null;
  // TODO can be omitted from BE side
  primaryImageVersionsProcessingStatus: string | null;
  role: EPageRole;
  // TODO can be omitted from BE side
  scheduleType: string;
  subheadline: string;
  title: string;
  navBarTitle?: string;
  builderTitle?: string;
  // is used for showing in Burger menu under day pages titles
  subTitle?: string;
  // TODO can be omitted from BE side
  titlePosition?: string | null;
  titleMountNodeId?: string;
  // TODO can be omitted from BE side
  travelDates: string;
  type: EPageType;
}

export interface IParams {
  includeOnlyDifferentYears: boolean;
  translate?: boolean;
  translationFormat?: string;
  translationDateFormat?: EDateFormat;
}

export interface IDetailsScheduleItems {
  accommodation: IAccommodationAndDetails;
  datesRange: IRange | null;
  daysRange: IRange | null;
  details: IAccommodationAndDetails;
  id: number | string;
  type: string;
}

export interface INearestLeftRange extends IDetailsScheduleItems {
  position: number;
}

export interface IAccommodationAndDetails {
  contentState: RawDraftContentState;
  removedPageIds: number[];
}

export interface ILanding {
  pages: IPage[];
}

export interface ICreator {
  email: string;
  fullName: string;
  id: number;
  status: boolean;
}

export interface IAccount {
  companyName: string;
  id: string;
  ownerEmail: string;
  subscriptions: ISubscriptions;
  type: 'admin' | 'agent' | 'owner' | 'guest';
}

export interface IActionButton {
  active?: boolean;
  buttonColor?: string;
  text?: string;
  textColor?: string;
  hyperlink?: string;
}

export interface IProposal {
  id: string;
  aboutSafariPages: Array<{ id: number; handle: string; type: string }>;
  aboutUsPages: Array<{ id: number; handle: string; type: string }>;
  // TODO fill in account
  account: IAccount;
  approved: boolean;
  actionButton?: IActionButton;
  confirmed: boolean;
  // TODO fill in costs
  costs: ICosts;
  costsAccept: boolean;
  costsCurrency: ICosts['costsCurrency'];
  createdAt: string;
  creator: ICreator;
  currentStep: string;
  customFonts: string[];
  customStyles: string | null;
  landingConfig: ILandingConfig;
  deactivated: boolean;
  deletedAt: string | null;

  detailsMode: EDetailsMode;
  detailsModeManuallyUpdated: boolean;
  // TODO fill in emergencyContacts
  emergencyContacts: Object[];
  // TODO fill in filtersForDetailsTab
  filtersForDetailsTab: Object | null;
  filtersForPagesAfterCostsPage: Object | null;
  filtersForPagesAfterCoverPage: Object | null;
  filtersForPagesAfterMapPage: Object | null;
  firstViewed: boolean;
  guestPortalId: string | null;
  guestPortalLastAvailableStep: string | null;
  itineraryId?: string | null;
  itineraryCostsPageHidden?: boolean;
  lastAvailableStep: string;
  miscPages: Object[];
  notes: string;
  name?: string;
  originalId: string | null;
  overview?: ITravelBriefDay<TDate>[];
  partnerAgent: Object | null;
  partnerAgentId: number | null;
  policies: Object;
  preparedFor: string | null;
  scheduleType: EOrganizedBy;
  schedules: IDetailsScheduleItems[];
  settings: ILandingSettings;
  shareUrl: string;
  landingShareUrl: string;
  shared: boolean;
  // whether to show mobile app banner
  showMobileInfo?: boolean;
  subscriptions: Object;
  tags: string[];
  title: string;
  tourRequest: Object;
  tourRequestId: number;
  travelBrief: ITravelBrief;
  travelDates: string;
  travelEndDate: string;
  travelStartDate: string;
  tripEvents: TTripEvent[];
  type: string;
  updatedAt: string;
  way: Object[];
  whiteLabelingOn: boolean;
  landing: ILanding;
  brandSettings: IBrandSettings;
  showHeadsUpModal?: boolean;
  app: TApp | null;
}

export type DocumentPolices = {
  destroy: boolean;
  show: boolean;
  update: boolean;
};

export type TravelWalletDocument = {
  accountId: string;
  category?: string;
  filename: string;
  height?: number;
  width?: number;
  id: string;
  mimeType?: string;
  name: string;
  policies?: DocumentPolices;
  previewUrl?: string;
  storageType?: string;
  url: string;
  versionsProcessingStatus?: string;
};

export interface IGuestPortal extends IProposal {
  additionalInfo: IAdditionalInfoProps[];
  guests: IGuestsProps[];
  specialOccasions: string;
  voucherDocuments: TravelWalletDocument[];
  ticketDocuments: TravelWalletDocument[];
  waiverDocuments: TravelWalletDocument[];
  miscDocuments: TravelWalletDocument[];
  additionalLinks: string[];
  passportDocuments: TravelWalletDocument[];
  insuranceDocuments: TravelWalletDocument[];
  guestMiscDocuments: TravelWalletDocument[];
}

export enum EProposalItems {
  Itinerary = 'itinerary',
  GuestPortal = 'guest_portal',
  Lookbook = 'lookbook',
}

export type ScheduleItemType = (typeof SCHEDULE_ITEM_TYPES)[keyof typeof SCHEDULE_ITEM_TYPES];

export type TApp = {
  androidPackageIdentifier: string;
  appleAppStoreIdentifier: 6450455051;
  id: number | null;
  iosBundleIdentifier: string;
  name: string;
  schema: string;
};
